import { BookOpenIcon, BuildingIcon } from '@phosphor-icons/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AppSidebar, navItems } from '@/components/app-sidebar';
import { ErrorState } from '@/components/error-state';
import { ImpersonationBanner } from '@/components/impersonation-banner';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { useGitBaseUrl, useOrgList } from '@/hooks/use-org';
import { sessionQueryOptions } from '@/lib/auth-query-options';
import { setUser } from '@/lib/faro';
import { listOrgsQueryOptions, orgBySlugQueryOptions } from '@/lib/org-query-options';
import { syncActiveOrg } from '@/server/functions/organization';
import { loadOrgLayout } from '@/server/functions/pages/org-layout';

function PendingOrgLayout() {
  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg">
                <div className="flex aspect-square size-8 shrink-0 items-center justify-center bg-mesa-green text-white">
                  <BuildingIcon weight="fill" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <Skeleton className="h-4 w-24 rounded" />
                  <span className="truncate text-xs text-mesa-muted">Organization</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton>
                      <item.Icon />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton>
                <BookOpenIcon />
                <span>Documentation</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <SidebarSeparator />
          <div className="flex items-center gap-3 px-2 py-2">
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="h-4 w-24 rounded" />
          </div>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="flex items-center border-b border-mesa-border p-2 md:hidden">
          <SidebarTrigger />
        </header>
        <main className="flex-1 p-6 lg:p-8">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <Skeleton className="h-7 w-40 rounded" />
              <Skeleton className="h-9 w-36 rounded" />
            </div>
            <div className="space-y-3">
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-4 w-5/6 rounded" />
              <Skeleton className="h-4 w-2/3 rounded" />
            </div>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export const Route = createFileRoute('/$orgSlug')({
  ssr: 'data-only',
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const result = await queryClient.ensureQueryData({
      queryKey: ['org-layout', params.orgSlug],
      queryFn: () => loadOrgLayout({ data: { orgSlug: params.orgSlug } }),
      staleTime: 10_000,
    });

    if (!result?.session) {
      throw redirect({ to: '/sign-in' });
    }

    if (!result.org) {
      throw redirect({ to: '/' });
    }

    const { session, org, orgs } = result;

    // Seed granular caches so components and mutations can access data independently
    queryClient.setQueryData(sessionQueryOptions.queryKey, session);
    queryClient.setQueryData(orgBySlugQueryOptions(params.orgSlug).queryKey, org);
    queryClient.setQueryData(listOrgsQueryOptions.queryKey, orgs);

    if (session.session.activeOrganizationId !== org.id) {
      await syncActiveOrg({ data: { orgId: org.id } });
      void queryClient.invalidateQueries({ queryKey: ['auth-session'] });
    }

    return { session, org };
  },
  pendingComponent: PendingOrgLayout,
  component: OrgLayout,
});

function OrgLayout() {
  const { session, org: routeOrg } = Route.useRouteContext();
  const { orgSlug } = Route.useParams();
  const { data: org } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const navigate = useNavigate();
  const { orgs, isLoading: isLoadingOrgs, error: orgsError, refetch: refetchOrgs } = useOrgList();
  const gitBaseUrl = useGitBaseUrl();

  useEffect(() => {
    setUser({ id: session.user.id, email: session.user.email });
  }, [session.user.id, session.user.email]);

  useEffect(() => {
    if (!isLoadingOrgs && !orgsError && orgs.length === 0) {
      navigate({ to: '/onboarding' });
    }
  }, [isLoadingOrgs, orgsError, orgs.length, navigate]);

  if (orgsError) {
    return (
      <ErrorState
        error={new Error('Unable to load organization')}
        reset={() => {
          if (refetchOrgs) {
            void refetchOrgs();
          }
        }}
      />
    );
  }

  if (!isLoadingOrgs && orgs.length === 0) {
    return null;
  }

  // beforeLoad redirects if org is null, so this is a type guard only
  const activeOrg = org ?? routeOrg;

  const impersonatedBy = (session.session as { impersonatedBy?: string }).impersonatedBy;

  return (
    <div className="flex h-screen flex-col">
      {impersonatedBy && (
        <ImpersonationBanner
          userName={session.user.name || session.user.email}
          email={session.user.email}
          orgName={activeOrg.name}
          orgId={activeOrg.id}
        />
      )}
      <div className="flex-1 overflow-hidden">
        <SidebarProvider className="!h-full">
          <AppSidebar
            session={session}
            currentOrg={activeOrg}
            orgs={isLoadingOrgs ? [activeOrg] : orgs}
            gitBaseUrl={gitBaseUrl}
            onOrgUpdated={() => {
              if (refetchOrgs) void refetchOrgs();
            }}
          />
          <SidebarInset>
            <header className="flex items-center border-b border-mesa-border p-2 md:hidden">
              <SidebarTrigger />
            </header>
            <main className="flex-1 overflow-auto p-6 lg:p-8">
              <Outlet />
            </main>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </div>
  );
}
